/** Redaction and highlight boxes drawn over the page. Each box is an OpenSeadragon overlay placed in image pixels, so
 *  it stays on its spot through zoom, pan and rotation. Drag on the page to draw; drag a box to move it; drag a corner
 *  to resize it. Mouse-wheel zoom keeps working while a drawing tool is on. */
import OpenSeadragon from 'openseadragon';

export type BoxKind = 'redaction' | 'highlight';
export interface Box { id: string; x: number; y: number; w: number; h: number; kind: BoxKind }

let seq = 0;
const newId = () => 'b' + Date.now().toString(36) + (++seq).toString(36);

export class Boxes {
  private boxes = new Map<string, { box: Box; el: HTMLDivElement }>();
  private sel: string | null = null;
  private drawing: { start: OpenSeadragon.Point; startPx: OpenSeadragon.Point; el: HTMLDivElement } | null = null;
  /** which kind a drag on the page draws, and which boxes can be changed (null: pan, nothing is editable) */
  private drawKind: BoxKind | null = null;

  constructor(private viewer: OpenSeadragon.Viewer, private stage: HTMLElement, private onChange: () => void, private onSelect: () => void) {
    viewer.addHandler('canvas-press', e => {
      if (!this.drawKind || !this.hasImage()) return;
      const img = this.toImage(e.position);
      const el = document.createElement('div');
      el.className = 'drawrect';
      stage.appendChild(el);
      this.drawing = { start: img, startPx: e.position, el };
      this.select(null);
    });
    viewer.addHandler('canvas-drag', e => {
      if (!this.drawing) return;
      e.preventDefaultAction = true;   // draw instead of panning
      const a = this.drawing.startPx, b = e.position;
      Object.assign(this.drawing.el.style, { left: Math.min(a.x, b.x) + 'px', top: Math.min(a.y, b.y) + 'px', width: Math.abs(b.x - a.x) + 'px', height: Math.abs(b.y - a.y) + 'px' });
    });
    viewer.addHandler('canvas-release', e => {
      const d = this.drawing;
      if (!d) return;
      this.drawing = null;
      d.el.remove();
      const end = this.toImage(e.position), size = this.imageSize();
      const x = Math.max(0, Math.min(d.start.x, end.x)), y = Math.max(0, Math.min(d.start.y, end.y));
      const x2 = Math.min(size.x, Math.max(d.start.x, end.x)), y2 = Math.min(size.y, Math.max(d.start.y, end.y));
      const px = Math.abs(e.position.x - d.startPx.x), py = Math.abs(e.position.y - d.startPx.y);
      if (px < 6 || py < 6 || x2 - x < 4 || y2 - y < 4 || !this.drawKind) return;   // a click, not a box
      this.add({ id: newId(), x: Math.round(x), y: Math.round(y), w: Math.round(x2 - x), h: Math.round(y2 - y), kind: this.drawKind });
      this.onChange();
    });
    viewer.addHandler('canvas-click', e => { if (this.drawKind) e.preventDefaultAction = true; });
  }

  private hasImage(): boolean { return this.viewer.world.getItemCount() > 0; }
  private item() { return this.viewer.world.getItemAt(0); }
  private imageSize(): OpenSeadragon.Point { return this.item().getContentSize(); }
  private toImage(p: OpenSeadragon.Point): OpenSeadragon.Point { return this.item().viewerElementToImageCoordinates(p); }
  private pointerImage(e: PointerEvent): OpenSeadragon.Point {
    const r = this.viewer.element.getBoundingClientRect();
    return this.toImage(new OpenSeadragon.Point(e.clientX - r.left, e.clientY - r.top));
  }
  private place(b: Box, el: HTMLElement): void {
    const rect = this.item().imageToViewportRectangle(b.x, b.y, b.w, b.h);
    if (this.viewer.getOverlayById(el)) this.viewer.updateOverlay(el, rect); else this.viewer.addOverlay({ element: el, location: rect });
  }

  /** Which tool is on: what a drag draws, and which boxes are live (movable, selectable). */
  setMode(kind: BoxKind | null): void {
    this.drawKind = kind;
    this.stage.classList.toggle('drawing', !!kind);
    for (const { box, el } of this.boxes.values()) el.classList.toggle('live', box.kind === kind);
    if (this.sel && this.boxes.get(this.sel)?.box.kind !== kind) this.select(null);
  }

  /** Replace every box (after a page opens). */
  set(list: Box[]): void {
    this.clearAll();
    for (const b of list) this.add({ ...b });
  }
  clearAll(): void {
    for (const { el } of this.boxes.values()) { try { this.viewer.removeOverlay(el); } catch { /* the viewer closed */ } }
    this.boxes.clear();
    this.sel = null;
  }
  remove(kinds: BoxKind[]): number {
    let n = 0;
    for (const [id, { box, el }] of [...this.boxes]) if (kinds.includes(box.kind)) { this.viewer.removeOverlay(el); this.boxes.delete(id); n++; }
    if (this.sel && !this.boxes.has(this.sel)) this.sel = null;
    return n;
  }
  list(kind?: BoxKind): Box[] { return [...this.boxes.values()].map(v => ({ ...v.box })).filter(b => !kind || b.kind === kind); }
  selected(): Box | null { const v = this.sel ? this.boxes.get(this.sel) : undefined; return v ? { ...v.box } : null; }
  deleteSelected(): boolean {
    const v = this.sel ? this.boxes.get(this.sel) : undefined;
    if (!v) return false;
    this.viewer.removeOverlay(v.el);
    this.boxes.delete(v.box.id);
    this.sel = null;
    this.onChange(); this.onSelect();
    return true;
  }

  private select(id: string | null): void {
    if (this.sel === id) return;
    if (this.sel) this.boxes.get(this.sel)?.el.classList.remove('selected');
    this.sel = id;
    if (id) this.boxes.get(id)?.el.classList.add('selected');
    this.onSelect();
  }

  private add(b: Box): void {
    const el = document.createElement('div');
    el.className = 'bx ' + b.kind + (b.kind === this.drawKind ? ' live' : '');
    el.dataset.id = b.id;
    for (const h of ['nw', 'ne', 'sw', 'se']) { const d = document.createElement('span'); d.className = 'hdl ' + h; d.dataset.h = h; el.appendChild(d); }
    el.addEventListener('pointerdown', e => this.grab(e, b.id));
    this.boxes.set(b.id, { box: b, el });
    this.place(b, el);
  }

  /** Move a box, or resize it from a corner, following the pointer in image pixels. */
  private grab(e: PointerEvent, id: string): void {
    const v = this.boxes.get(id);
    if (!v || v.box.kind !== this.drawKind) return;
    e.preventDefault(); e.stopPropagation();
    this.select(id);
    const handle = (e.target as HTMLElement).dataset.h ?? null;
    const start = this.pointerImage(e), orig = { ...v.box }, size = this.imageSize();
    this.viewer.setMouseNavEnabled(false);
    let moved = false;
    const move = (ev: PointerEvent) => {
      const p = this.pointerImage(ev), dx = p.x - start.x, dy = p.y - start.y;
      let { x, y, w, h } = orig;
      if (!handle) { x = Math.max(0, Math.min(size.x - w, x + dx)); y = Math.max(0, Math.min(size.y - h, y + dy)); }
      else {
        if (handle.includes('w')) { const nx = Math.max(0, Math.min(x + w - 4, x + dx)); w += x - nx; x = nx; }
        if (handle.includes('e')) w = Math.max(4, Math.min(size.x - x, w + dx));
        if (handle.includes('n')) { const ny = Math.max(0, Math.min(y + h - 4, y + dy)); h += y - ny; y = ny; }
        if (handle.includes('s')) h = Math.max(4, Math.min(size.y - y, h + dy));
      }
      Object.assign(v.box, { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
      this.place(v.box, v.el);
      moved = true;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      this.viewer.setMouseNavEnabled(true);
      if (moved) this.onChange();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
}
